import { useEffect, useState } from "react";
import Seo from "@/lib/seo/Seo";
import { getSeoForPath } from "@/lib/seo/routes.mjs";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, ArrowRight, ChevronRight, Send, MessageSquare } from "lucide-react";
import { SITE, API_BASE } from "@/lib/site";
import { getTrackingPayload } from "@/lib/utm";
import { initTracking, trackConversion, pushLeadDataLayer } from "@/lib/tracking";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import Reveal from "@/components/Reveal";
import ParticleField from "@/components/ParticleField";
import { ContactIllustration } from "@/components/illustrations/FintechIllustrations";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  // Load tracking pixels once on mount; conversion fires only on success.
  useEffect(() => { initTracking(); }, []);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in name, email, and message.");
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API_BASE}/contact`, { ...form, tracking: getTrackingPayload() });
      trackConversion("ContactLead");
      pushLeadDataLayer("lead_contact_submit", "contact", r?.data?.id);
      toast.success("Thank you for contacting Swipetronics. We received your information and will review it shortly.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <Seo {...getSeoForPath("/contact")} />
      {/* HERO — unified: text + illustration blended into one navy header */}
      <section className="relative bg-[var(--swipe-navy)] text-white overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 grid-faint opacity-30 pointer-events-none" />
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse 55% 65% at 78% 50%, rgba(0,114,255,0.30) 0%, transparent 65%)",
          }}
        />
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse 40% 50% at 20% 30%, rgba(0,114,255,0.14) 0%, transparent 60%)",
          }}
        />
        <div aria-hidden="true" className="absolute -top-24 -left-20 w-[420px] h-[420px] rounded-full pointer-events-none" style={{ background: "rgba(0,114,255,0.18)", filter: "blur(110px)" }} />
        <div aria-hidden="true" className="absolute -bottom-32 right-[12%] w-[480px] h-[480px] rounded-full pointer-events-none" style={{ background: "rgba(0,114,255,0.16)", filter: "blur(120px)" }} />
        <div aria-hidden="true" className="absolute top-[30%] right-[2%] w-2 h-2 rounded-full bg-[var(--swipe-accent)] opacity-70 pointer-events-none" />
        <div aria-hidden="true" className="absolute top-[18%] left-[40%] w-1.5 h-1.5 rounded-full bg-[#7DB6FF] opacity-60 pointer-events-none" />
        <div aria-hidden="true" className="absolute bottom-[20%] left-[8%] w-1.5 h-1.5 rounded-full bg-[#7DB6FF] opacity-50 pointer-events-none" />

        <ParticleField
          density={36}
          connectDistance={130}
          className="absolute inset-0 w-full h-full"
        />

        <div className="container-swipe relative pt-14 pb-16 lg:pt-20 lg:pb-24">
          <nav className="flex items-center gap-2 text-[13px] text-white/50 mb-8">
            <Link to="/" className="hover:text-white">Home</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-white/80">Contact</span>
          </nav>

          <div className="grid lg:grid-cols-[1.05fr,1fr] gap-10 lg:gap-12 items-center">
            <Reveal>
              <div className="relative">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/8 border border-white/15 text-[10.5px] uppercase tracking-[0.22em] font-bold text-[#7DB6FF] mb-6">
                  <span className="w-1.5 h-1.5 rounded-full bg-[var(--swipe-accent)] animate-pulse" />
                  Contact Us
                </div>
                <h1 className="font-display-tight text-[2.5rem] sm:text-[3.25rem] lg:text-[3.75rem] leading-[1.05] font-extrabold text-white tracking-[-0.02em]">
                  Contact{" "}
                  <span className="text-[var(--swipe-accent)]">Swipetronics</span>
                </h1>
                <p className="mt-6 text-[17px] text-white/75 leading-[1.7] max-w-xl">
                  Get in touch with Swipetronics to learn more about payment processing, POS
                  systems, mobile devices, and business solutions built to help your operation run
                  smoother.
                </p>
                <p className="mt-3 text-[14.5px] text-white/60 leading-relaxed max-w-xl">
                  Based locally? See our{" "}
                  <Link to="/merchant-services-syracuse-ny" className="text-[var(--swipe-accent)] font-semibold hover:underline" data-testid="contact-syracuse-link">
                    Syracuse &amp; Central New York merchant services
                  </Link>{" "}
                  page.
                </p>
                <div className="mt-8 flex flex-wrap gap-3">
                  <a
                    href={`tel:${SITE.phone}`}
                    className="btn-primary-swipe text-[15px] py-3.5 px-7"
                    data-testid="contact-hero-cta-call"
                  >
                    <Phone className="w-4 h-4" /> Call {SITE.phone}
                  </a>
                  <a
                    href={`mailto:${SITE.email}`}
                    className="btn-outline-light text-[15px] py-3.5 px-7"
                    data-testid="contact-hero-cta-email"
                  >
                    <Mail className="w-4 h-4" /> Email Us <ArrowRight className="w-4 h-4" />
                  </a>
                </div>
              </div>
            </Reveal>

            <Reveal variant="slide-right" delay={120}>
              <div className="relative">
                <ContactIllustration
                  transparent
                  className="relative w-full h-auto max-w-[560px] mx-auto"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="section container-swipe">
        <div className="grid lg:grid-cols-[1fr,1.2fr] gap-10">
          <div>
            <div className="overline mb-4">Reach out</div>
            <h2 className="font-display-tight text-3xl sm:text-4xl font-black text-[var(--swipe-navy)] leading-tight">
              Let&apos;s start with a conversation.
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              The fastest way to start saving is to talk to a real person. We&apos;ll listen, review what you have today, and only recommend a change if it actually helps.
            </p>

            <ul className="mt-10 space-y-5">
              <li className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-[var(--swipe-navy)] text-white flex items-center justify-center shrink-0">
                  <Phone className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider font-bold text-slate-500">Phone</div>
                  <a href={`tel:${SITE.phoneRaw}`} className="mt-0.5 block font-display text-xl font-bold text-[var(--swipe-navy)] hover:text-[var(--swipe-accent)]" data-testid="contact-phone">{SITE.phone}</a>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-[var(--swipe-navy)] text-white flex items-center justify-center shrink-0">
                  <Mail className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider font-bold text-slate-500">Email</div>
                  <a href={`mailto:${SITE.email}`} className="mt-0.5 block font-display text-xl font-bold text-[var(--swipe-navy)] hover:text-[var(--swipe-accent)]" data-testid="contact-email">{SITE.email}</a>
                </div>
              </li>
            </ul>

            <div className="mt-10 bg-[var(--swipe-navy)] text-white rounded-2xl p-6 relative overflow-hidden">
              <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-[var(--swipe-accent)]/20 blur-3xl" />
              <div className="relative">
                <MessageSquare className="w-6 h-6 text-[var(--swipe-accent)]" />
                <div className="mt-3 font-display text-lg font-bold" style={{ color: "#FFFFFF" }}>Prefer to send your statement?</div>
                <p className="mt-2 text-sm" style={{ color: "#FFFFFF" }}>Use our Free Savings Analysis form to upload a statement and get a custom comparison.</p>
                <a href="/savings-analysis" className="mt-4 inline-flex items-center gap-1.5 text-[var(--swipe-accent)] font-semibold text-sm">Free Savings Analysis <ArrowRight className="w-4 h-4" /></a>
              </div>
            </div>
          </div>

          <form onSubmit={submit} className="bg-white border border-line rounded-2xl p-8 lg:p-10" data-testid="contact-form">
            <div className="overline mb-3">Send us a message</div>
            <h3 className="font-display-tight text-2xl font-black text-[var(--swipe-navy)]">We&apos;ll reply within one business day.</h3>

            <div className="mt-8 grid sm:grid-cols-2 gap-5">
              <div>
                <Label htmlFor="c-name">Full name *</Label>
                <Input id="c-name" value={form.name} onChange={update("name")} placeholder="Your name" required className="mt-2" data-testid="contact-input-name" />
              </div>
              <div>
                <Label htmlFor="c-email">Email *</Label>
                <Input id="c-email" type="email" value={form.email} onChange={update("email")} placeholder="you@business.com" required className="mt-2" data-testid="contact-input-email" />
              </div>
              <div>
                <Label htmlFor="c-phone">Phone</Label>
                <Input id="c-phone" value={form.phone} onChange={update("phone")} placeholder="(315) 555-0123" className="mt-2" data-testid="contact-input-phone" />
              </div>
              <div>
                <Label htmlFor="c-subject">Subject</Label>
                <Input id="c-subject" value={form.subject} onChange={update("subject")} placeholder="How can we help?" className="mt-2" data-testid="contact-input-subject" />
              </div>
            </div>

            <div className="mt-5">
              <Label htmlFor="c-message">Message *</Label>
              <Textarea id="c-message" value={form.message} onChange={update("message")} placeholder="Tell us about your business..." rows={5} required className="mt-2" data-testid="contact-input-message" />
            </div>

            <button type="submit" disabled={submitting} className="btn-primary-swipe mt-7 w-full justify-center sm:w-auto disabled:opacity-60 disabled:cursor-not-allowed" data-testid="contact-submit">
              {submitting ? "Sending..." : <>Send message <Send className="w-4 h-4" /></>}
            </button>
            <p className="mt-4 text-[11.5px] text-slate-500 leading-[1.6]" data-testid="contact-consent">
              By submitting this form, you agree to be contacted by Swipetronics regarding your request. Message and data rates may apply. Reply STOP to opt out of text messages.
            </p>
          </form>
        </div>
      </section>
    </div>
  );
}
